import React, { useState, useEffect } from 'react';
import { 
  Users, Settings, Activity, Zap, RefreshCw, BarChart3,
  UserPlus, Search, Download, Eye, Edit, Trash2, ChevronRight, Lock, Shield
} from 'lucide-react';
import toast from 'react-hot-toast';
import UserManagement from './UserManagement';
import Integrations from './Integrations';
import Configuration from './Configuration';
import TeamManagementTab from '../components/TeamManagementTab';
import UserRoleManagement from '../components/UserRoleManagement';
import { getAllRoles, getRoleLabel, getRoleColorClasses } from '../utils/roleConfig';
import { getUserRoles, isAdmin, isTeamLead } from '../utils/roleUtils';
import { getUserStatistics, sortUsersByName } from '../utils/userUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// MetricCard Component
const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600',
    purple: 'from-purple-500 to-purple-600',
    teal: 'from-teal-500 to-teal-600',
    red: 'from-red-500 to-red-600'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-lg p-6 text-white`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-white/70 text-xs mt-1">{subtitle}</p>}
        </div>
        <Icon className="h-12 w-12 text-white/40" />
      </div>
    </div>
  );
};

const AdminAndrewDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Data states
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leadRecruiters, setLeadRecruiters] = useState([]);
  const [allRecruiters, setAllRecruiters] = useState([]);
  const [accountManagers, setAccountManagers] = useState([]);
  const [recruiterSupervisors, setRecruiterSupervisors] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [integrations, setIntegrations] = useState([
    {
      id: 1,
      name: 'Microsoft Entra ID',
      type: 'Authentication',
      status: 'active',
      lastSync: new Date().toISOString()
    },
    {
      id: 2,
      name: 'Email Service',
      type: 'Communication',
      status: 'configured',
      lastSync: new Date().toISOString()
    }
  ]);

  // Filter states
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    supervisor: 'all',
    searchTerm: ''
  });

  // Metrics
  const [adminMetrics, setAdminMetrics] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    totalRecruiters: 0,
    totalAccountManagers: 0,
    totalLeads: 0,
    activeIntegrations: 0,
    pendingActions: 0
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUsers(),
        fetchTeams()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      const usersData = data.users || [];
      
      // Sort users
      const sortedUsers = sortUsersByName(usersData);
      setUsers(sortedUsers);
      
      // Calculate metrics using utility function
      const stats = getUserStatistics(usersData);
      setAdminMetrics({
        totalUsers: stats.total,
        activeUsers: stats.active,
        inactiveUsers: stats.inactive,
        totalRecruiters: stats.byRole['recruiter'] || 0,
        totalAccountManagers: (stats.byRole['account_manager'] || 0) + (stats.byRole['account_exec'] || 0),
        totalLeads: (stats.byRole['lead_recruiter'] || 0) + (stats.byRole['lead'] || 0),
        activeIntegrations: integrations.filter(i => i.status === 'active').length,
        pendingActions: 0
      });
      
      // Store all users
      setAllUsers(usersData);
      
      // Filter by roles
      const leads = usersData.filter(u => u.role === 'lead_recruiter' || u.role === 'lead');
      const recruiters = usersData.filter(u => u.role === 'recruiter');
      const accountMgrs = usersData.filter(u => u.role === 'account_manager' || u.role === 'Account Manager' || u.role === 'account_exec');
      const supervisors = usersData.filter(u => u.role === 'recruiter_supervisor');
      
      setLeadRecruiters(leads);
      setAllRecruiters(recruiters);
      setAccountManagers(accountMgrs);
      setRecruiterSupervisors(supervisors);
      
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/teams`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch teams');
      }
      
      const data = await response.json();
      setTeams(data.teams || []);
      
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    }
  };

  const handleRefresh = () => {
    toast.success('Refreshing dashboard data...');
    fetchAllData();
  };

  // Team member management handlers
  const handleAddMember = async (teamId, userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/teams/${teamId}/members`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'add',
          member_id: userId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add member');
      }

      const data = await response.json();
      
      // Update teams in state
      setTeams(prevTeams => 
        prevTeams.map(team => 
          team.id === teamId 
            ? { ...team, team_members: data.team_members }
            : team
        )
      );

      return data;
    } catch (error) {
      console.error('Error adding team member:', error);
      throw error;
    }
  };

  const handleRemoveMember = async (teamId, userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/teams/${teamId}/members`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'remove',
          member_id: userId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to remove member');
      }

      const data = await response.json();
      
      // Update teams in state
      setTeams(prevTeams => 
        prevTeams.map(team => 
          team.id === teamId 
            ? { ...team, team_members: data.team_members }
            : team
        )
      );

      return data;
    } catch (error) {
      console.error('Error removing team member:', error);
      throw error;
    }
  };

  // Overview Tab Component
  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard 
          title="Total Users"
          value={adminMetrics.totalUsers}
          icon={Users}
          color="blue"
          subtitle={`${adminMetrics.activeUsers} active`}
        />
        <MetricCard 
          title="Recruiters"
          value={adminMetrics.totalRecruiters}
          icon={Users}
          color="green"
          subtitle="Active recruiters"
        />
        <MetricCard 
          title="Account Managers"
          value={adminMetrics.totalAccountManagers}
          icon={Shield}
          color="orange"
          subtitle="Account management team"
        />
        <MetricCard 
          title="Team Leads"
          value={adminMetrics.totalLeads}
          icon={Users}
          color="purple"
          subtitle="Leadership roles"
        />
      </div>

      {/* Quick Stats */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600">Active Teams</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{teams.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Active Integrations</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{adminMetrics.activeIntegrations}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Inactive Users</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{adminMetrics.inactiveUsers}</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            View All
          </button>
        </div>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <Activity className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">System initialized</p>
              <p className="text-xs text-gray-600 mt-1">Dashboard is ready for use</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-xs text-gray-600">System administration & management</p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'users'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Users
            </button>
            {/* <button
              onClick={() => setActiveTab('user-roles')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'user-roles'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              User Roles
            </button> */}
            <button
              onClick={() => setActiveTab('team-management')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'team-management'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Team Management
            </button>
            <button
              onClick={() => setActiveTab('integrations')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'integrations'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Integrations
            </button>
            <button
              onClick={() => setActiveTab('configuration')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'configuration'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Configuration
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading dashboard data...</p>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && <OverviewTab />}
            {activeTab === 'users' && <UserManagement />}
            {activeTab === 'user-roles' && <UserRoleManagement />}
            {activeTab === 'team-management' && (
              <TeamManagementTab 
                leadRecruiters={leadRecruiters}
                accountManagers={accountManagers}
                recruiterSupervisors={recruiterSupervisors}
                teams={teams}
                allRecruiters={allRecruiters}
                allUsers={allUsers}
                onAddMember={handleAddMember}
                onRemoveMember={handleRemoveMember}
                onRefresh={fetchAllData}
              />
            )}
            {activeTab === 'integrations' && <Integrations integrations={integrations} />}
            {activeTab === 'configuration' && <Configuration />}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAndrewDashboard;
